import type { EdtActivity, LocalAnyEntry } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import { Button, EmptyState, Spinner, TextInput, useToast } from '../components/index.ts';
import { anaestheticRepository } from '../db/repositories/anaesthetic.ts';
import { casesRepository } from '../db/repositories/cases.ts';
import { communicationsRepository } from '../db/repositories/communications.ts';
import { edtRepository } from '../db/repositories/edt.ts';
import { outboxRepository } from '../db/repositories/outbox.ts';
import { proceduresRepository } from '../db/repositories/procedures.ts';
import { EntryFilterSheet } from '../features/entries/EntryFilterSheet.tsx';
import { DayGroupRows } from '../features/entries/EntryRows.tsx';
import { VirtualList } from '../features/entries/VirtualList.tsx';
import type { DayGroup, EntryFilters, EntryKind } from '../features/entries/model.ts';
import {
  NO_FILTERS,
  activeFilterCount,
  buildDayGroups,
  countEntries,
} from '../features/entries/model.ts';
import { SearchIcon } from '../layout/icons.tsx';
import { InstallCard } from '../pwa/InstallCard.tsx';

/**
 * The Entries tab: everything logged, newest first, grouped by day.
 *
 * Driven by `useLiveQuery`, so it updates the instant anything changes locally —
 * including when the sync puller writes in phase 12, and when an entry is
 * deleted two screens away.
 */
export function EntriesRoute() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const userId = user?.id ?? null;
  const [filters, setFilters] = useState<EntryFilters>(NO_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const data = useLiveQuery(async () => {
    if (userId === null) {
      return {
        cases: [],
        procedures: [],
        edtEntries: [],
        anaestheticCases: [],
        communications: [],
        failedIds: new Set<string>(),
      };
    }

    const [cases, procedures, edtEntries, anaestheticCases, communications, failedIds] =
      await Promise.all([
        casesRepository.list(userId),
        proceduresRepository.list(userId),
        edtRepository.list(userId),
        anaestheticRepository.list(userId),
        communicationsRepository.list(userId),
        outboxRepository.failedEntityIds(userId),
      ]);

    return { cases, procedures, edtEntries, anaestheticCases, communications, failedIds };
  }, [userId]);

  const groups = useMemo(
    () =>
      data === undefined
        ? []
        : buildDayGroups(data, filters),
    [data, filters],
  );

  const usedProcedureKeys = useMemo(
    () => [...new Set((data?.procedures ?? []).map((procedure) => procedure.procedureKey))],
    [data],
  );

  const usedActivities = useMemo<EdtActivity[]>(
    () => [...new Set((data?.edtEntries ?? []).map((entry) => entry.activity))],
    [data],
  );

  const total =
    data === undefined
      ? 0
      : data.cases.length +
        data.procedures.length +
        data.edtEntries.length +
        data.anaestheticCases.length +
        data.communications.length;
  const shown = countEntries(groups);
  const filterCount = activeFilterCount(filters);
  const filtering = filterCount > 0 || filters.query.trim() !== '';

  async function duplicate(entry: LocalAnyEntry, kind: EntryKind): Promise<void> {
    if (userId === null) return;

    if (kind === 'case') {
      const copy = await casesRepository.duplicateWithProcedures(userId, entry.id);
      if (copy !== undefined) void navigate('/log/case', { state: { prefill: copy } });
      return;
    }

    if (kind === 'edt') {
      const copy = await edtRepository.duplicate(userId, entry.id);
      if (copy !== undefined) void navigate('/log/edt', { state: { prefill: copy } });
      return;
    }

    if (kind === 'communication') {
      const copy = await communicationsRepository.duplicate(userId, entry.id);
      if (copy !== undefined) void navigate('/log/communication', { state: { prefill: copy } });
      return;
    }

    // Anaesthetic cases are import-only, so their rows offer no Duplicate and
    // this is unreachable from the UI. Guarded rather than assumed.
    if (kind === 'anaesthetic') return;

    const copy = await proceduresRepository.duplicate(userId, entry.id);
    if (copy !== undefined) void navigate('/log/procedure', { state: { prefill: copy } });
  }

  async function remove(entry: LocalAnyEntry, kind: EntryKind): Promise<void> {
    if (userId === null) return;

    if (kind === 'edt') {
      await edtRepository.softDelete(userId, entry.id);

      showToast({
        message: 'Entry deleted.',
        actions: [{ label: 'Undo', onAction: () => void edtRepository.restore(userId, entry.id) }],
      });
      return;
    }

    if (kind === 'anaesthetic') {
      await anaestheticRepository.softDelete(userId, entry.id);

      showToast({
        message: 'Anaesthetic case deleted.',
        actions: [
          {
            label: 'Undo',
            onAction: () => void anaestheticRepository.restore(userId, entry.id),
          },
        ],
      });
      return;
    }

    if (kind === 'communication') {
      await communicationsRepository.softDelete(userId, entry.id);

      showToast({
        message: 'Entry deleted.',
        actions: [
          {
            label: 'Undo',
            onAction: () => void communicationsRepository.restore(userId, entry.id),
          },
        ],
      });
      return;
    }

    if (kind === 'case') {
      const removed = await casesRepository.softDelete(userId, entry.id);

      showToast({
        message:
          removed.length === 0
            ? 'Case deleted.'
            : `Case and ${String(removed.length)} procedure${removed.length === 1 ? '' : 's'} deleted.`,
        actions: [
          {
            label: 'Undo',
            onAction: () => void casesRepository.restoreWithProcedures(userId, entry.id, removed),
          },
        ],
      });
      return;
    }

    await proceduresRepository.softDelete(userId, entry.id);

    showToast({
      message: 'Procedure deleted.',
      actions: [
        { label: 'Undo', onAction: () => void proceduresRepository.restore(userId, entry.id) },
      ],
    });
  }

  if (data === undefined) return <Spinner label="Loading your logbook" />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-faint" />
          <TextInput
            type="search"
            value={filters.query}
            onChange={(event) => setFilters({ ...filters, query: event.target.value })}
            placeholder="Search entries"
            aria-label="Search entries"
            autoCapitalize="none"
            spellCheck={false}
            className="pl-10"
          />
        </div>

        <Button variant="secondary" onClick={() => setFiltersOpen(true)}>
          Filters
          {filterCount === 0 ? null : (
            <span className="rounded-full bg-accent px-1.5 text-xs text-accent-contrast">
              {filterCount}
            </span>
          )}
        </Button>
      </div>

      {!filtering ? null : (
        <div className="flex items-center justify-between gap-3 px-1">
          <span className="text-xs text-faint">
            {shown === 0
              ? 'Nothing matches'
              : `${String(shown)} of ${String(total)} ${total === 1 ? 'entry' : 'entries'}`}
          </span>
          <button
            type="button"
            onClick={() => setFilters(NO_FILTERS)}
            className="min-h-touch px-1 text-sm text-accent hover:text-accent-hover"
          >
            Clear
          </button>
        </div>
      )}

      {/* Appears once, after a fifth entry — never on a first visit. */}
      <InstallCard />

      {total === 0 ? (
        <EmptyState
          title="No entries yet"
          description="Log a procedure, a case or some EDT and it will appear here, newest first."
          action={
            <Button variant="primary" onClick={() => void navigate('/log')}>
              Log something
            </Button>
          }
        />
      ) : groups.length === 0 ? (
        <EmptyState
          title="Nothing matches"
          description="Try a shorter search, or clear a filter or two."
          action={
            <Button variant="secondary" onClick={() => setFilters(NO_FILTERS)}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <VirtualList<DayGroup>
          items={groups}
          keyOf={(group) => group.key}
          renderItem={(group) => (
            <DayGroupRows
              group={group}
              failedIds={data.failedIds}
              actions={{
                // The kind is the route segment, for all three.
                onOpen: (entry, kind) => void navigate(`/log/${kind}/${entry.id}`),
                onDuplicate: (entry, kind) => void duplicate(entry, kind),
                onDelete: (entry, kind) => void remove(entry, kind),
              }}
            />
          )}
        />
      )}

      <EntryFilterSheet
        open={filtersOpen}
        filters={filters}
        onChange={setFilters}
        onClose={() => setFiltersOpen(false)}
        usedProcedureKeys={usedProcedureKeys}
        usedActivities={usedActivities}
      />
    </div>
  );
}
